import { makeAutoObservable, runInAction, toJS, autorun } from 'mobx'
import { findLast, keyBy, isEmpty, cloneDeep, unionBy, sumBy, sum, pick, forEach, flatten, map } from '@dt/fe-utils/dist/lodash'
import { pk as doPk, toDecimal, ddayjs, deepClone } from '@dt/fe-utils'
import { restaurantCenter, productMobx, restaurantConfig, discountMobx, feeMobx, RESTAURANT_EVENT, cartMobx } from '@mobx/mall'
import { orderMobx, restaurantConfig as rConfig } from 'restaurant'
import { memberMobx } from '@mobxStore'
import { TCartitemDisabledReason } from 'mobx/mall/restaurantCenter'
import { getPlatform } from '@utils'
import LocationApi from '@api/location.api'
import orderAPI from 'api/order.api'

export enum TSaveCartError {
  OVER_LIMIT = 'OVER_LIMIT',
  DATA_ERROR = 'DATA_ERROR'
}

// 根据餐品id 规格id和口味组返回餐品pk <productId>_<scaleId>_<modifierGroupsPk> , products?: ISingleCartItem[] | undefined
export function createFkey ({
  productNo,
  scaleNo,
}: {
  productNo: string;
  scaleNo: string;
}) {
  return `${productNo}_${scaleNo}`
}

function getSourceType () {
  const { isWeixin, isWeixinMiniProgram } = getPlatform()
  let type = 'onlineMobileWebH5'
  if (isWeixinMiniProgram) {
    type = 'onlineWechatMiniProgram'
  } else if (isWeixin && type !== 'scanQRcode') {
    type = 'onlineWechatOfficialAccount'
  }
  return type
}

// 创建一个新的购物车项
function createCartItem ({ type, productNo, fkey, scaleNo, price, cnt = 1, pk, activityType = '', remark = '', saleCnt = 1, addUserId = '', addUserName = '', addUserAvatar = '', isTaxFree = false, groupBuyId, saleTotalCnt, timestamp }: { type: string; productNo: string; price?: number; scaleNo: string; saleCnt?: number; saleTotalCnt?: number; cnt?: number; remark: string; activityType?: string; addUserId?: string; pk: string; addUserName?: string; addUserAvatar?: string; isTaxFree?: boolean; fkey: string; groupBuyId?: string; timestamp?: number }) {
  // 处理口味，先过滤掉未选择的item项
  const productData = productMobx.noToViewProductMap[productNo]
  if (!productData) {
    console.error('product is not exist')
    return
  }
  const theScale = productData.scales.find(scale => scale.no === scaleNo)
  if (!theScale) {
    console.error('the scale is not exist')
    return
  }
  return {
    type,
    productNo,
    name: productData.name,
    price,
    cnt,
    saleCnt,
    saleTotalCnt,
    remark,
    pk: pk || doPk(),
    scaleNo: theScale.no,
    scaleName: theScale.name,
    addUserId,
    addUserName,
    addUserAvatar,
    isTaxFree,
    fkey,
    activityType,
    groupBuyId,
    timestamp: timestamp || ddayjs().valueOf(),
  } as IMallCartItem
}

// 获取购物车初始化
function getCartInitialInfo (eatType?: TEatType) {
  return {
    tableCode: '',
    tableTent: '',
    remark: '',
    person: restaurantCenter.config.shouldDispPersonModal === true ? 0 : 1,
    startTimeOffset: 0,
    payMode: 0 as 0 | 1,
    isTogo: 0, // 购物车上有全局的标志位
    startTime: '',
    menuTime: '',
    eatType: eatType || 'mall' as TEatType, // 购物车当前用餐模式
    freeTaxList: []
  }
}

function getViewItems (items: IMallCartItem[]): IViewMallCartItem[] {
  return items.map((item: IMallCartItem) => {
    const viewProduct = productMobx.noToViewProductMap[item.productNo]
    let disabledReason = ''
    let disabled = !viewProduct
    let isOff = !viewProduct // 商品是否下架
    if (disabled) {
      disabledReason = TCartitemDisabledReason.EXPIRED
    }
    // 有可能餐品已经失效，所以不能以viewProduct作为必要数据
    const scale = (viewProduct && viewProduct.scales.find((s: any) => s.no === item.scaleNo)) || { price: 0, togoFee: 0, name: '', modifierGroups: null, productPrice: (item as any).productPrice }
    // 规格下架case
    if ((!scale || !(scale as IScale).no || (scale as IScale).cnt <= 0) && !disabled) {
      disabled = true
      disabledReason = TCartitemDisabledReason.EXPIRED
    }
    const scaleName = disabledReason === TCartitemDisabledReason.EXPIRED ? item.scaleName : scale.name || ''
    const name = viewProduct?.name || item.name
    // 当前规格加口味变成了 一组option的形式放到了购物车中
    let optionStr = scaleName || ''
    let modifierStr = ''
    let optionHtml = `<span>${scaleName}</span>`
    let addPrice = 0
    let modifierPrice = 0
    let productGroups: any
    let productGroupsBoxPrice = 0
    // 这边需要额外注意只有openPrice才会在item上附带有price
    const price = item.price ? item.price + addPrice : addPrice
    const productPrice = (item.price || scale?.price || (scale as any)?.productPrice || 0) + addPrice
    const unitPrice = productPrice
    const tax = feeMobx.pkToTax[item.pk] || 0
    const freeTax = feeMobx.pkToFreeTax[item.pk] || 0
    const unitTax = toDecimal(tax / item.cnt, 7)
    const unitBoxPrice = toDecimal(productGroupsBoxPrice || scale.togoFee || 0, 7)
    const unitPriceWithTax = unitPrice + tax
    // TODO 需要对一下这个数据的正确性
    const totalTax = tax // * item.cnt
    const totalFreeTax = freeTax
    const totalProductPrice = toDecimal(productPrice * item.cnt)
    const totalPrice = toDecimal(unitPrice * item.cnt)
    const totalPriceWithTax = toDecimal(totalPrice + totalTax)
    const totalBoxPrice = 0
    // 优惠对接
    const itemDiscountInfo = discountMobx.discountResult.itemMap[item.pk]
    let offPrice = 0
    let apportionOffPrice = itemDiscountInfo && itemDiscountInfo.apportion ? itemDiscountInfo.apportion.off : 0
    const offItems = itemDiscountInfo && itemDiscountInfo.items ? itemDiscountInfo.items.map(offInfo => {
      offPrice += offInfo.off
      return {
        offTip: offInfo.offTip,
        off: offInfo.off,
        offType: offInfo.offType
      }
    }) : []
    const offItemPlan = itemDiscountInfo && itemDiscountInfo.items ? itemDiscountInfo.items.find(offItem => offItem.offType === 'plan') : null
    const offItemPlanTagId = offItemPlan ? offItemPlan.tagId || 0 : 0
    const actualPrice = toDecimal(totalPrice - offPrice)
    // const apportionOffPrice = apportionOffPrice
    const realPrice = toDecimal(actualPrice - apportionOffPrice)
    // 未下单的餐品实际的价格就是未退的价格
    const totalActualPrice = totalPrice
    const unitRealPrice = toDecimal(realPrice / item.cnt)
    const unitRealPriceWithTax = toDecimal(unitRealPrice + unitTax, 7)
    const realPriceWithTax = toDecimal(unitRealPrice + totalTax, 7)
    const pic = viewProduct ? viewProduct.pic : (item as any).pic
    const categories = viewProduct ? viewProduct.categories : []
    const statisticsCategoryNo = viewProduct ? viewProduct.statisticsCategoryNo : ''
    const allergys = viewProduct?.allergys || []
    const tags = viewProduct?.tags || []
    return {
      ...item,
      modifierStr,
      optionStr,
      price,
      productPrice,
      totalActualPrice,
      totalProductPrice,
      modifierPrice,
      unitPrice,
      tax,
      unitPriceWithTax,
      totalPrice,
      totalTax,
      totalPriceWithTax,
      unitBoxPrice,
      disabled,
      disabledReason,
      isOff,
      optionHtml,
      offPrice,
      offItems,
      actualPrice,
      apportionOffPrice,
      realPrice,
      unitTax,
      unitRealPrice,
      unitRealPriceWithTax,
      realPriceWithTax,
      pic,
      categories,
      allergys,
      tags,
      statisticsCategoryNo,
      scaleName,
      name,
      freeTax,
      totalFreeTax,
      offItemPlanTagId,
      productGroups,
      itemInfoBackup: { ...item },
      totalBoxPrice
    }
  })
}

function getOrderDetails <T extends IViewMallCartItem> (viewCartItems: T[]): ISaveMallCartItem[] {
  let items = [] as ISaveMallCartItem[]
  viewCartItems
    .filter(item => item.cnt && !item.disabled)
    .forEach(cartItem => {
      const item = {
        cnt: cartItem.cnt,
        productNo: cartItem.productNo,
        isTogo: 0,
        originalFee: cartItem.totalProductPrice,
        paidFee: cartItem.totalProductPrice,
        box: cartItem.totalBoxPrice,
        offItems: cartItem.offItems, // 实际用于核对使用
        productName: cartItem.name,
        productScaleNo: cartItem.scaleNo,
        startTime: cartItem.startTimeOffset ? ddayjs().add(cartItem.startTimeOffset, 'm').format() : null, // cartItem.startTime,
        type: cartItem.type,
        pk: cartItem.pk,
        remark: cartItem.remark,
        startTimeOffset: cartItem.startTimeOffset,
        addUserId: cartItem.addUserId,
        addUserName: cartItem.addUserName?.substring(0, 50),
        addUserAvatar: cartItem.addUserAvatar,
        isTaxFree: cartItem.isTaxFree ? 1 : 0,
        addPrice: cartItem.addPrice
      } as ISaveMallCartItem
      items.push(item)
    })
  return items
}

// 将订单中的餐品还原回购物车的统一转换方法
function convertOrderItemToCartItem (orderItem: IViewMallOrderDetail): IMallCartItem {
  return {
    type: orderItem.type,
    productNo: orderItem.productNo,
    name: orderItem.name,
    cnt: orderItem.cnt,
    remark: orderItem.remark,
    pk: orderItem.pk,
    fkey: orderItem.fkey,
    scaleNo: orderItem.scaleNo,
    scaleName: orderItem.scaleName,
    odetailId: orderItem.detailId,
    addUserId: orderItem.addUserId,
    addUserName: orderItem.addUserName,
    addUserAvatar: orderItem.addUserAvatar,
    isTaxFree: orderItem.isTaxFree
  }
}

// 将未接单的订单(其中可能包含未接单的餐品)还原回购物车的统一转换方法
function convertSaveItemToCartItem (saveItem: ISaveCartItem): IMallCartItem {
  return {
    type: saveItem.type,
    productNo: saveItem.productNo,
    name: saveItem.productName,
    cnt: saveItem.cnt,
    remark: saveItem.remark,
    pk: saveItem.pk,
    fkey: createFkey({ productNo: saveItem.productNo, scaleNo: saveItem.productScaleNo }),
    scaleNo: saveItem.productScaleNo,
    scaleName: '',
    addUserId: saveItem.addUserId,
    addUserName: saveItem.addUserName,
    addUserAvatar: saveItem.addUserAvatar,
    isTaxFree: !!saveItem.isTaxFree,
    addPrice: saveItem.addPrice
  }
}

/** 点餐购物车Mobx */
class Cart {
  constructor () {
    makeAutoObservable(this)
  }
  debug = false

  /** 购物车的标记由购物车信息组成 cartItems & info */
  lastHash = ''

  /** 当前购物车同时存在的订单的ID */
  ordersId: string = ''

  /** 基础的购物车项 */
  cartItems: IMallCartItem[] = [];
  /** 用于支付失败页重新下单 */
  saveCartItems: IMallCartItem[] = [];

  /** 基础的购物信息 */
  info: ICartInfo = getCartInitialInfo()

  /** 购物车仓库，用于暂存（挂单/取单） */
  cartDepot: ICartCopy[] = []

  /** 特殊的标记位主要用于隔离是否已下单未下单混杂计算，默认开启 */
  isMixture = true

  /** 选择了打包的餐品fkeys */
  cartPackFkeys: { [key: string]: number } = {}

  /** 是否需要打包 */
  needPack: boolean = false

  /** 活动类型 group 拼团 */
  activityType: string = ''

  /** 订单合并展示 */
  orderMerge: boolean = false

  contactError: any = { nameError: false, mobileError: false, addressError: false }

  contactLoading: boolean = false

  member = memberMobx.isLogin ? {
    type: memberMobx.memberInfo.type,
    id: String(memberMobx.memberInfo.memberId),
    mobile: memberMobx.memberInfo.mobile,
    email: memberMobx.memberInfo.email,
    key: bo === 'cn' ? (String(memberMobx.memberInfo.memberId) || memberMobx.memberInfo.visitorId) : (memberMobx.memberInfo.visitorId || String(memberMobx.memberInfo.memberId)),
    nickName: memberMobx.memberInfo.nickName,
    avatar: memberMobx.memberInfo.avatarUrl,
    openId: memberMobx.memberInfo.openId
  } : {}

  /** 计算后的购物车项(业务视图中使用此项) */
  get viewCartItems (): IViewMallCartItem[] {
    if (!this.isMixture && orderMobx.order.status !== 'NotAccepted') {
      return []
    }
    return getViewItems(this.cartItems)
  }

  /** 计算后的购物车不可用项 （仅业务层视图使用此项） */
  get viewDisabledCartItems (): IViewMallCartItem[] {
    return this.viewCartItems.filter(item => item.disabled)
  }

  /** 经过判断后的已下的订单的数据，实际是从orderMobx过来的值 */
  get viewOrderItems (): IViewMallOrderDetail[] {
    if (orderMobx && orderMobx.order.ordersId === this.ordersId && this.info.payMode) {
      return orderMobx.viewDetailList.filter(viewDetail => !viewDetail.isWait && viewDetail.originalCnt).map(viewDetail => {
        // 优惠对接
        const itemDiscountInfo = discountMobx.discountResult.itemMap[viewDetail.pk]
        let offPrice = 0
        let apportionOffPrice = itemDiscountInfo && itemDiscountInfo.apportion ? itemDiscountInfo.apportion.off : 0
        const offItems = itemDiscountInfo && itemDiscountInfo.items ? itemDiscountInfo.items.map(offInfo => {
          offPrice += offInfo.off
          return {
            offTip: offInfo.offTip,
            off: offInfo.off,
            offType: offInfo.offType,
          }
        }) : []
        const tax = feeMobx.pkToTax[viewDetail.pk] || viewDetail.tax
        const totalTax = toDecimal(tax, 7)
        const unitTax = viewDetail.cnt ? toDecimal(totalTax / viewDetail.cnt, 7) : 0
        const actualPrice = toDecimal(viewDetail.totalPrice - offPrice)
        const totalActualPrice = viewDetail.refundCnt > 0 ? 0 : actualPrice
        // const apportionOffPrice = discountMobx.discountResult.apportion[viewDetail.pk] ? discountMobx.discountResult.apportion[viewDetail.pk].off : 0
        const realPrice = toDecimal(actualPrice - apportionOffPrice)
        const unitRealPrice = toDecimal(realPrice / viewDetail.cnt)
        const unitRealPriceWithTax = toDecimal(unitRealPrice + unitTax, 7)
        const realPriceWithTax = toDecimal(realPrice + totalTax, 7)
        const allergys = []
        const tags = []
        // TODO: 税分类ID
        const taxCategoryId = productMobx.noToViewProductMap[viewDetail.productNo]?.taxCategoryId || 0
        /** 已下订单的餐品的税以再次计算的结果为准 */
        const fkey = createFkey({ productNo: viewDetail.productNo, scaleNo: viewDetail.scaleNo })
        return {
          ...viewDetail,
          tax,
          unitTax,
          offPrice,
          offItems,
          actualPrice,
          totalActualPrice,
          apportionOffPrice,
          realPrice,
          unitRealPrice,
          unitRealPriceWithTax,
          realPriceWithTax,
          allergys,
          tags,
          taxCategoryId,
          fkey
        }
      })
    } else {
      return []
    }
  }

  get availableItems () {
    if (this.isMixture) {
      return [...this.viewCartItems, ...this.viewOrderItems].filter(item => item.cnt && !(item as any).disabled)
    } else {
      return this.viewOrderItems.filter(item => item.cnt && !(item as any).disabled)
    }
  }

  /** 返回当前购物车中所有餐品是否有设置过餐品备注 */
  get cartProductHasReamrkStatus (): boolean {
    return this.cartItems.some(cartitem => {
      return cartitem.remark.length
    })
  }

  /** 计算后的购物概述，包括数量、价格等 */
  get cartResult (): ICartResult {
    const allProductNoToCntMap: any = {}
    const productNoToCntMap: any = {}
    const scaleNoToCntMap: { [pscaleNo: string]: number } = {}
    const allScaleNoToCntMap: { [pscaleNo: string]: number } = {}
    const categoryNoToCntMap: any = {}
    const swapBuyProductScaleNoToCntMap: any = {}
    const fullItemNFreeProductScaleNoToCntMap: any = {}
    const orderSwapBuyProductNoToCntMap: any = {}
    const orderSwapBuyProductScaleNoToCntMap: any = {}
    // (餐品 + 服务费 + 打包费 + 配送费 + 税 + 小费 - 优惠）
    let totalPrice = 0
    let totalOrderPrice = 0
    let totalOrderBoxPrice = 0
    let totalCartPrice = 0
    let totalCartBoxPrice = 0
    // 当次购物车中的餐品总价
    let presentItemPrice = 0
    // 餐品原价
    let itemPrice = 0
    // 配送费
    let expressPrice = this.info.express?.price || 0
    // 打包费
    let boxPrice = 0
    // 当前购物车餐品总数
    let presentTotalCnt = 0
    // 当前购物车餐品 + 已下订单中餐品的总数
    let totalCnt = 0
    // 税
    const tax = feeMobx.calculatedTaxTotal
    const freeTax = feeMobx.calculatedFreeTaxTotal
    const showTax = feeMobx.calculatedShowTaxTotal
    // 整单优惠（包括店长折扣）
    const offItems = discountMobx.discountResult.offArr // .filter(off => off.offType !== 'coupon')
    const floorOffItem = discountMobx.discountResult.floorOff
    const integralMoneyOffItem = discountMobx.discountResult.integralMoneyOff
    const orderPlanOffItem = discountMobx.discountResult.orderPlanOff
    const discountCouponPrice = sumBy(offItems.filter(off => off.offType === 'coupon'), 'off')
    const discountActivityPrice = sumBy(offItems.filter(off => off.offType === 'activity'), 'off')
    let activityPrice = sumBy(offItems.filter(off => off.offType === 'activity'), 'off')
    let discountMemberCardPrice = sumBy(offItems.filter(off => ['memberCard', 'memberPrice'].includes(off.offType)), 'off')
    const discountPlanPrice = orderPlanOffItem?.off || 0
    const discountFloorPrice = floorOffItem?.off || 0
    const discountIntegralMoneyPrice = integralMoneyOffItem?.off || 0
    const discountPromoCodeWholePrice = sumBy(offItems.filter(off => off.offType === 'promoCode'), 'off')
    // 计算购物车项
    const calcViewCartItems = (viewCartItems: IViewMallCartItem[], childNode?: boolean) => {
      viewCartItems.filter(item => item.cnt && !item.disabled).forEach(item => {
        totalCnt += item.disabled ? 0 : item.cnt
        presentTotalCnt += item.disabled ? 0 : item.cnt
        // 手机: 换购、满送餐品不计入菜单已点数量 20211122
        if (!(restaurantCenter.config.app === 'mobile' && childNode)) {
          productNoToCntMap[item.productNo] = (productNoToCntMap[item.productNo] || 0) + item.cnt
        }
        scaleNoToCntMap[item.productNo + '_' + item.scaleNo] = (scaleNoToCntMap[item.productNo + '_' + item.scaleNo] || 0) + item.cnt
        if (restaurantConfig.posAllCategories && restaurantConfig.posAllCategoriesNo) {
          const allNo = restaurantConfig.posAllCategoriesNo
          categoryNoToCntMap[allNo] = (categoryNoToCntMap[allNo] || 0) + item.cnt
        }
        item.categories.forEach(category => {
          categoryNoToCntMap[category] = (categoryNoToCntMap[category] || 0) + item.cnt
        })
        if (item.disabled) {
          return
        }
        boxPrice += item.totalBoxPrice
        presentItemPrice += item.totalPrice
        totalPrice += item.totalPrice + item.totalBoxPrice
        itemPrice += item.totalPrice
        totalCartBoxPrice += item.totalBoxPrice
        totalCartPrice += item.totalPrice + item.totalBoxPrice
      })
    }
    // 计算购物车订单项
    const calcViewOrderItems = (viewOrderItems: IViewMallOrderDetail[]) => {
      viewOrderItems.forEach(item => {
        totalCnt += item.cnt
        if (item.refundCnt <= 0) {
          boxPrice += item.totalBoxPrice
          totalPrice += item.totalPrice + item.totalBoxPrice
          itemPrice += item.totalPrice
          totalOrderBoxPrice += item.totalBoxPrice
          totalOrderPrice += item.totalPrice + item.totalBoxPrice
        }
      })
    }
    // 单品中的promoCode优惠金额
    let discountPromoCodeItemPrice = 0
    forEach(discountMobx.discountResult.itemMap, itemDiscount => itemDiscount.items && itemDiscount.items.forEach(item => {
      if (item.offType === 'promoCode') {
        discountPromoCodeItemPrice += item.off
      }
      if (['memberCard', 'memberPrice'].includes(item.offType)) {
        discountMemberCardPrice += item.off
      }

      if (item.offType === 'activity') {
        activityPrice += item.off
      }
    }))
    const discountPromoCodePrice = toDecimal(discountPromoCodeItemPrice + discountPromoCodeWholePrice)
    const discountWholePrice = toDecimal(discountCouponPrice + discountActivityPrice + discountFloorPrice + discountIntegralMoneyPrice + discountPromoCodeWholePrice + discountPlanPrice)
    const discountItemCouponPrice = sum(Object.values(discountMobx.discountResult.itemMap).map(item => sumBy((item.items || []).filter(off => off.offType === 'coupon'), 'off')))
    const discountExpressPrice = sumBy((discountMobx.discountResult.itemMap[`${this.info?.express?.pk}`]?.items || []), 'off')
    // 餐品级优惠
    let discountItemPrice = 0
    let discountCartItemPrice = 0
    let discountOrderItemPrice = 0
    discountCartItemPrice = this.viewCartItems.filter(item => !item.disabled).reduce((prv: number, cartItem) => {
      let price = cartItem.offPrice
      return prv + price
    }, 0)
    if (this.info.payMode) {
      discountOrderItemPrice = this.viewOrderItems.reduce((prv: number, orderItem) => {
        let price = orderItem.offPrice
        return prv + price
      }, 0)
    }
    // 购物车商品总优惠+已下单商品总优惠
    discountItemPrice = toDecimal(discountCartItemPrice + discountOrderItemPrice)
    // 总优惠
    let totalDiscountPrice = toDecimal(discountWholePrice + discountItemPrice + discountExpressPrice)
    // 当前如果已下单
    const serviceCharge = feeMobx.calculatedServiceChargeTotal
    const serviceChargeDetail = deepClone(feeMobx.calculatedServiceCharge)
    calcViewCartItems(this.viewCartItems)
    calcViewOrderItems(this.viewOrderItems)
    let tableAlias = ''
    totalPrice += tax
    totalPrice += serviceCharge
    totalPrice += expressPrice
    // 当前是否可直接发起支付，先付费 或 后付费已下过单切当前购物车无餐品
    // let isCanPay = this.ordersId && !presentTotalCnt
    // if (presentTotalCnt) {
    //   isCanPay = false
    // }
    let isCanPay = true
    // 可下单
    let isCanSave = presentTotalCnt
    const nono = this.ordersId ? orderMobx.viewOrder.nono : undefined
    const isTogo = 0
    totalPrice = toDecimal(totalPrice - totalDiscountPrice)
    const paidPrice = toDecimal(totalPrice - tax)
    const addTime = this.ordersId ? orderMobx.viewOrder.addTime : ddayjs().format()
    const taxItems = []
    // const taxItems = feeMobx.taxCategoryResult.map(item => {
    //   const hasServiceCharge = item.items.some(item => !item.pk)
    //   const hasExpressCharge = item.items.some(item => item.productNo === 'express')
    //   const items = item.items.filter(item => item.productNo !== 'express')
    //   return {
    //     name: item.name,
    //     taxCategoryId: item.taxCategoryId,
    //     amount: item.amount,
    //     rate: toDecimal(item.rate * 100),
    //     count: sumBy(items, 'cnt') + (hasServiceCharge ? 1 : 0) + (hasExpressCharge ? 1 : 0),
    //     hasServiceCharge,
    //     hasExpressCharge,
    //     isTaxFree: items.every(value => !!value.isTaxFree)
    //   }
    // })
    return {
      allProductNoToCntMap,
      // 商城不参与必点校验；补齐共享购物车结果的必点统计字段。
      requiredProductNoToCntMap: {},
      requiredCartProductNoToCntMap: {},
      requiredOrderProductNoToCntMap: {},
      productNoToCntMap,
      requiredSelectionShortages: [],
      categoryNoToCntMap,
      allScaleNoToCntMap,
      scaleNoToCntMap,
      swapBuyProductScaleNoToCntMap,
      fullItemNFreeProductScaleNoToCntMap,
      orderSwapBuyProductNoToCntMap,
      orderSwapBuyProductScaleNoToCntMap,
      itemActualPrice: toDecimal(itemPrice - discountItemPrice - showTax),
      itemTotalPrice: toDecimal(itemPrice - totalDiscountPrice - showTax),
      discountItemPrice,
      discountWholePrice,
      discountPlanPrice,
      discountCouponPrice,
      discountItemCouponPrice,
      discountActivityPrice,
      discountFloorPrice,
      discountIntegralMoneyPrice,
      discountPromoCodeWholePrice,
      discountPromoCodeItemPrice,
      discountPromoCodePrice,
      totalDiscountPrice: toDecimal(totalDiscountPrice),
      serviceCharge,
      serviceChargeDetail,
      expressPrice,
      boxPrice,
      tax,
      freeTax,
      showTax,
      totalPrice: toDecimal(totalPrice),
      paidPrice,
      tip: 0,
      totalPriceExcludeTip: toDecimal(totalPrice - 0),
      itemPrice,
      presentItemPrice,
      totalOriginalPrice: toDecimal(paidPrice + totalDiscountPrice),

      presentTotalCnt,
      tableAlias,
      totalCnt,
      isCanPay: !!isCanPay,
      isCanSave: !!isCanSave,
      // 必点规则仅适用于点单页；商城侧保持兼容的通过状态。
      requiredProductResult: { items: [], lackCnt: 0, isReady: true },
      modified: Date.now(),
      nono,
      addTime,
      offItems,
      floorOffItem,
      integralMoneyOffItem,
      // promoCodeOffItem,
      orderPlanOffItem,
      isTogo,
      taxItems,
      totalCartPrice: toDecimal(totalCartPrice - discountCartItemPrice),
      totalCartBoxPrice,
      totalOrderPrice: toDecimal(totalOrderPrice - discountOrderItemPrice),
      totalOrderBoxPrice,
      discountMemberCardPrice,
      activityPrice
    }
  }

  get isWait (): boolean {
    return this.info.payMode === 1 && orderMobx.viewOrder.isWait
  }

  /** pk到购物车视图项的map */
  get pkToViewCartItemMap () {
    return keyBy(this.viewCartItems, 'pk') as {
      [pk: string]: IViewCartItem;
    }
  }

  /** 渠道 */
  get channel () {
    let channel = productMobx.channel
    if (rConfig.buyTogetherId) {
      channel = 'buyTogether'
    } else if (rConfig.codeType === 'brand' && ['pickup', 'delivery'].includes(this.info.eatType)) {
      channel = 'selfDelivery'
    }
    return channel
  }

  /** 添加餐品到购物车, 支持doProduct对象 数组集合 isReset新加 */
  addToCart (doProduct: IMallDoProduct | IMallDoProduct[] | IMallCartItem[], targetType: string = ''): TSaveCartError | undefined {
    if (!Array.isArray(doProduct)) doProduct = [doProduct]
    this.activityType = targetType
    doProduct.forEach((product) => {
      const type = product.type
      const productNo = product.no
      const scaleNo = product.scaleNo
      const saleCnt = product.saleCnt
      const saleTotalCnt = product.saleTotalCnt
      const remark = product.remark || ''
      const doScale = product.scales.find((scale) => scale.no === scaleNo)
      const price = doScale && doScale.price || 0
      const timestamp = product.timestamp
      const groupBuyId = product.groupBuyId
      const fkey = createFkey({ productNo, scaleNo })
      if (!scaleNo) {
        console.error('scale id is null')
        return TSaveCartError.DATA_ERROR
      }
      const pk = product.pk
      const addUserId = product.addUserId || this.member?.key || ''
      const addUserName = product.addUserName || this.member?.nickName || ''
      const addUserAvatar = product.addUserAvatar || this.member?.avatar || ''
      const isTaxFree = (product as IMallDoProduct).isTaxFree || false
      if (pk) {
        this.viewCartItems
          .filter(item => item.fkey === fkey)
          .filter(item => item.pk !== pk)
          .forEach(item => {
            const cartItem = this.cartItems.find(i => i.pk === item.pk)
            if (cartItem) {
              this.remove(cartItem)
              product.cnt += cartItem.cnt
            }
          })

        const cartItemIndex = this.cartItems.findIndex(item => item.pk === pk)
        const newCartItem = createCartItem({ type, productNo, fkey, price, scaleNo, activityType: targetType, cnt: product.cnt, saleCnt, saleTotalCnt, pk, remark, addUserId, addUserName, addUserAvatar, isTaxFree, groupBuyId, timestamp })
        if (newCartItem) {
          newCartItem.cnt = product.cnt
          this.cartItems.splice(cartItemIndex, 1, newCartItem)
          restaurantCenter.emit(restaurantCenter.RESTAURANT_EVENT.CART_ITEMS_CHANGED, {
            type: 'add',
            cartItem: newCartItem,
            itemPlan: product.itemPlan
          })
        }
      } else {
        const newCartItem = createCartItem({ type, productNo, fkey, price, scaleNo, activityType: targetType, cnt: product.cnt, saleCnt, saleTotalCnt, pk: product.spk || pk, remark, addUserId, addUserName, addUserAvatar, isTaxFree, groupBuyId, timestamp })
        if (newCartItem) {
          this.insert(newCartItem)
          restaurantCenter.emit(restaurantCenter.RESTAURANT_EVENT.CART_ITEMS_CHANGED, {
            type: 'add',
            cartItem: newCartItem,
            itemPlan: product.itemPlan
          })
        }
      }
    })

    return
  }

  /** 插入餐品到购物车 处理餐品插入逻辑(cartItems.push) */
  insert (cartItem: IMallCartItem) {
    this.cartItems.push(cartItem)
  }

  /** 删除餐品，支持cartItem也支持fkey 支持多人模式 */
  remove (cart: IMallCartItem | string | string[], fromSync = false) {
    if (Array.isArray(cart)) {
      cart.forEach(pk => this.remove(pk))
    } else {
      const index = this.cartItems.findIndex(item => item.pk === (typeof cart === 'string' ? cart : cart.pk))
      if (~index) {
        // 保证事件到达时数量是正确的，即为0
        const cpCartItem = cloneDeep(this.cartItems[index])
        cpCartItem.cnt = 0
        this.cartItems.splice(index, 1)
        restaurantCenter.emit(restaurantCenter.RESTAURANT_EVENT.CART_ITEMS_CHANGED, {
          type: 'remove',
          cartItem: cpCartItem,
          fromSync
        })
        // 自动计算满送
        // this.autoFullNFree()
      }
    }
  }

  /** 重置购物车，将清空购物车，同时重置购物车信息 */
  reset (eatType?: TEatType) {
    this.info = getCartInitialInfo(eatType)
    this.clearOrdersId()
    this.clear()
    this.lastHash = ''
    restaurantCenter.emit(RESTAURANT_EVENT.CART_RESET)
  }

  /** 清空购物车 */
  clear () {
    if (!this.cartItems.length) {
      return
    }
    this.cartItems = []
    restaurantCenter.emit(restaurantCenter.RESTAURANT_EVENT.CART_ITEMS_CHANGED, {
      type: 'clear'
    })
  }

  /** 递增餐品数量，支持cartItem 支持pk 支持多人模式 */
  add (cart: IMallCartItem | string | string[]) {
    // 增加的时候混杂了多人时，增加的 则是自己的餐品
    let cartItem: IMallCartItem | undefined
    if (Array.isArray(cart)) {
      cartItem = this.cartItems.find(cartItem => cart.includes(cartItem.pk) && cartItem.addUserId === this.member?.key)
      if (!cartItem) {
        const oneCartItem = this.cartItems.find(cartItem => cart.includes(cartItem.pk))
        if (oneCartItem) {
          cartItem = cloneDeep(oneCartItem)
          cartItem.addUserId = restaurantConfig?.member?.key || ''
          cartItem.addUserName = restaurantConfig?.member?.nickName || ''
          cartItem.addUserAvatar = restaurantConfig?.member?.avatar || ''
          cartItem.cnt = 1
          cartItem.pk = doPk()
          cartItem.fkey = createFkey({ productNo: cartItem.productNo, scaleNo: cartItem.scaleNo })
          this.insert(cartItem)
        }
      } else {
        cartItem.cnt++
      }
    } else {
      cartItem = this.cartItems.find(item => item.pk === (typeof cart === 'string' ? cart : cart.pk))
      cartItem && cartItem.cnt++
    }
    if (cartItem) {
      restaurantCenter.emit(restaurantCenter.RESTAURANT_EVENT.CART_ITEMS_CHANGED, {
        type: 'change',
        cartItem: cartItem,
        isAdd: true,
      })
      // 自动计算满送
      // this.autoFullNFree()
    }
  }

  /** 递减餐品数量，如果删除到0则删除该cartItem，支持cartItem 支持pk 支持菜单中的餐品 支持多人模式 */
  reduce (cart: IMallCartItem | string | IMallViewProduct | string[]) {
    let cartItem: IMallCartItem | undefined
    if (Array.isArray(cart)) {
      cartItem = this.cartItems.find(cartItem => cart.includes(cartItem.pk) && cartItem.addUserId === this.member?.key)
      if (!cartItem) {
        // TODO 当前无需指定先修改某一个用户的餐品
        cartItem = this.cartItems.find(cartItem => cart.includes(cartItem.pk))
      }
    } else if (typeof cart === 'string' || (cart as IMallCartItem).pk) {
      cartItem = this.cartItems.find(item => typeof cart === 'string' ? item.pk === cart : item.pk === (cart as IMallCartItem).pk)
    } else {
      cartItem = findLast(this.cartItems, item => item.productNo === (cart as IMallViewProduct).no)
    }
    if (cartItem) {
      if (cartItem.cnt <= 0) {
        this.remove(cartItem)
        return
      }
      cartItem.cnt--
      if (!cartItem.cnt) {
        const index = this.cartItems.findIndex(item => item.pk === (cartItem as IMallCartItem).pk)
        if (~index) {
          this.cartItems.splice(index, 1)
        }
        restaurantCenter.emit(restaurantCenter.RESTAURANT_EVENT.CART_ITEMS_CHANGED, {
          type: 'remove',
          cartItem
        })
        // 自动计算满送
        // this.autoFullNFree()
      } else {
        restaurantCenter.emit(restaurantCenter.RESTAURANT_EVENT.CART_ITEMS_CHANGED, {
          type: 'change',
          cartItem
        })
        // 自动计算满送
        // this.autoFullNFree()
      }
    }
  }

  setSourceType (sourceType?: string | undefined | null) {
    this.info.sourceType = sourceType || undefined
  }

  setOrdersOutNo (ordersOutNo: string) {
    this.info.ordersOutNo = ordersOutNo
  }

  /** pk到购物车视图项的map */
  get pkToAvailableItemMap () {
    return keyBy(this.availableItems, 'pk') as {
      [pk: string]: IViewCartItem | IViewMallOrderDetail;
    }
  }

  restoreOrderInfo () {
    this.info.person = orderMobx.viewOrder.person
    this.info.remark = orderMobx.viewOrder.remark
    this.info.tableCode = orderMobx.viewOrder.tableCode
    this.info.tableTent = orderMobx.viewOrder.tableTent
    this.info.payMode = orderMobx.viewOrder.payMode
    this.info.contactName = orderMobx.viewOrder.contactName
    this.info.contactMobile = orderMobx.viewOrder.contactMobile
    const contactInfo = JSON.parse(orderMobx.viewOrder.contactInfo || '{}')
    this.info.contactInfo = contactInfo
    this.info.contactAddress = contactInfo.address || ''
    this.info.contactApt = contactInfo.apt || ''
    this.info.contactEmail = contactInfo.email || ''
    // 对接三方外卖
    this.info.sourceType = orderMobx.viewOrder.sourceType
    this.info.ordersOutNo = orderMobx.viewOrder.ordersOutNo
    // 还原优惠
    if (orderMobx.viewOrder.discountList) {
      discountMobx.restoreDiscountAction(orderMobx.viewOrder.discountList)
    } else {
      discountMobx.clearDiscountAction()
    }
    // 还原免税
    const freeTaxList: number[] = []
    if (orderMobx.viewOrder.freeTax > 0) {
      this.viewOrderItems.forEach(orderItem => {
        if (orderItem.freeTax > 0) {
          if (!freeTaxList.includes(orderItem.taxCategoryId)) {
            freeTaxList.push(orderItem.taxCategoryId)
          }
        }
      })
    }
    this.info.freeTaxList = freeTaxList
    // 处理未接单的订单（其中可能包含未接单的餐品，将还原回购物车）
    if (orderMobx.viewOrder.isWait && orderMobx.viewOrder.ordersCart) {
      this.cartItems = orderMobx.viewOrder.ordersCart.map(this.convertSaveItemToCartItem)
    }
  }

  /** 设置订单id到购物车，购物车会自动计算当前该订单的数据 */
  setOrdersId (ordersId: string): Promise<any> {
    this.ordersId = ordersId
    // 先付费到此为此
    // if (!this.info.payMode) {
    //   return Promise.resolve()
    // }
    // 引发order mobx的数据模型的变化
    restaurantCenter.emit(restaurantCenter.RESTAURANT_EVENT.ORDER_GET_START, { ordersId })
    return orderMobx.init(ordersId, { showOrderCart: true }).then(() => {
      restaurantCenter.emit(restaurantCenter.RESTAURANT_EVENT.ORDER_GET_END, { ordersId })
      // 从order 搬运到cart
      runInAction(() => {
        this.restoreOrderInfo()
      })
    })
  }

  /** 同步把当前的订单放回到购物车中，不需要再次重新获取 */
  setOrder () {
    if (orderMobx.order.ordersId) {
      this.ordersId = orderMobx.order.ordersId
      this.restoreOrderInfo()
    }
  }

  clearOrdersId () {
    this.ordersId = ''
  }

  private getSaveInfo () {
    const saveData: any = {}
    const baseConfig = {
      startTime: undefined,
      deviceId: 'mobile',
      // 推广链接当前放置在sourceType中, 当前门店码和桌码归为 scanQRcode
      sourceType: getSourceType(),
      brandPromotionId: restaurantConfig.promotionId || undefined
    }
    const mktFee = toDecimal(this.cartResult.totalDiscountPrice)
    const contactInfoObj: any = {
      ...(this.info.contactInfo || {})
    }
    if (this.info.contactAddress) {
      contactInfoObj.address = this.info.contactAddress
    }
    if (this.info.contactApt) {
      contactInfoObj.apt = this.info.contactApt
    }
    if (this.member?.mobile) {
      contactInfoObj.memberMobile = this.member.mobile
    }
    if (this.member?.email) {
      contactInfoObj.memberEmail = this.member.email
    }
    saveData.infos = Object.assign(baseConfig, {
      brandId: restaurantConfig.brandId,
      channel: productMobx.channel,
      appType: productMobx.channel,
      memberId: discountMobx.discountResult.memberId ? discountMobx.discountResult.memberId.toString() : '0',
      memberGroupId: discountMobx.discountResult.memberCardId ? discountMobx.discountResult.memberCardId.toString() : '0',
      eatType: this.info.eatType,
      ordersType: 'mall',
      bo,
      currency: bo === 'cn' ? 'RMB' : bo === 'de' ? 'EUR' : 'USD',
      tableAlias: this.cartResult.tableAlias,
      isTogo: this.cartResult.isTogo,
      payMode: this.info.payMode > 0 ? 1 : 0,
      person: this.info.person,
      ordersId: this.ordersId,
      startTime: this.info.startTime || undefined,
      menuTime: this.info.menuTime || undefined,
      originalFee: toDecimal(this.cartResult.totalOriginalPrice),
      paidFee: toDecimal(this.cartResult.totalPrice - this.cartResult.tax),
      mktFee,
      sk: mktFee > 0, // TODO: 未来要去掉
      subject: 'mall',
      tax: this.cartResult.tax,
      freeTax: this.cartResult.freeTax,
      serviceCharge: this.cartResult.serviceCharge,
      box: this.cartResult.boxPrice,
      storeId: 0,
      // express: this.cartResult.expressPrice,
      remark: this.info.remark,
      contactInfo: JSON.stringify(contactInfoObj),
      contactMobile: this.info.contactMobile,
      contactName: this.info.contactName,
      freeTaxCategoryList: this.info.freeTaxList.map(i => i.toString()),
      openId: this.member.openId,
    })
    // 已存在订单不支持更改点餐类型
    if (this.ordersId && orderMobx.viewOrder.eatType) {
      saveData.infos.eatType = orderMobx.viewOrder.eatType
    }
    if (this.info.appType === '3rdapp') {
      saveData.discount = { closeActivity: true }
    } else {
      saveData.discount = Object.assign({ sk: discountMobx.discountResult.sk, version: discountMobx.discountResult.version }, discountMobx.actionPlan)
    }
    // 记录当前订单的状态使用虚拟的状态isWait 主要区别是后付费加菜还是后付费重新下菜
    saveData.isWait = this.isWait
    return saveData
  }

  /** 清除已失效的餐品 */
  clearDisabledItems () {
    this.cartItems = this.cartItems.filter((item: any) => {
      const cur = this.viewCartItems.find((v: any) => v.pk === item.pk)
      return cur && !cur.disabled
    })
  }
  /** 下单 */
  async save (): Promise<IMallOrder> {
    // 已下单取消订单重新下单
    if (this.ordersId) {
      const order = orderMobx.viewOrder
      const canCancel = ['Pending', 'NotAccepted'].includes(order?.status)
      if (canCancel) {
        const success = await this.cancel()
        success && this.clearOrdersId()
      }
    }
    if (this.cartResult.presentTotalCnt || (this.ordersId && this.isWait)) {
      let isMember = false
      // pos和mobile在下单中有差异部分顾现处理差异部分
      let saveData = this.getSaveInfo()
      saveData.ordersDetails = getOrderDetails(this.viewCartItems)
      if (cartMobx.info?.express?.pk) {
        saveData.ordersDetails.push({
          pk: cartMobx.info?.express?.pk,
          type: 'express',
          paidFee: cartMobx.cartResult.expressPrice,
          originalFee: cartMobx.cartResult.expressPrice
        } as any)
      }
      if (discountMobx.discountResult.groupBuyActivityId) {
        isMember = true
        const doCart = this.viewCartItems.find((item) => item.groupBuyId)
        if (doCart) {
          saveData.groupBuyId = doCart.groupBuyId
        } else {
          saveData.groupBuyActivityId = discountMobx.discountResult.groupBuyActivityId
        }
      }
      console.log('🚀mallSaveOrder', saveData)
      return orderAPI.save(saveData, isMember).then(res => {
        if (!this.info.payMode) {
          // 制作一个当前购物车的刻度
          this.makeHash()
        }
        runInAction(() => {
          orderMobx.restore(res.result)
          // TODO 需要权衡一下 当前把下单后的订单号和nono号返回用于后续支付的接入，暂不考虑缓存下单后的订单
          this.ordersId = res.result.ordersId
          restaurantCenter.emit(RESTAURANT_EVENT.SAVE_ORDER, {
            order: res.result
          })
        })
        return res.result
      }, (err) => {
        console.log('save error', err)
        if (err.code === -161 || err.code === -165 || err.code === -166 || err.code === -167 || err.code === -399) { // 价格变动,包括餐品价格、税、服务费、优惠变化
        } else if (err.code === -52) { // 餐厅已打烊
        } else if (err.code === -395) { // 当前餐品不可售卖
        } else if (err.code === -602 || err.code === -603 || err.code === -604) { // 部分餐品不可售
          if (err.data) {
            err.data = unionBy(err.data, (message: IProductSaleError) => message.productNo + message.scaleNo).map((message: IProductSaleError) => {
              const scaleNo = message.scaleNo === '-1' ? '0' : message.scaleNo
              const viewProduct = productMobx.noToViewProductMap[message.productNo]
              const viewSale = viewProduct ? viewProduct.scales.find(sale => sale.no === scaleNo) : ''
              const viewCart = this.viewCartItems.find(item => item.productNo === message.productNo && item.scaleNo === scaleNo)
              const pks = map(this.viewCartItems.filter(item => item.productNo === message.productNo && item.scaleNo === scaleNo), 'pk')
              const sPks = map(flatten(map(this.viewCartItems).filter(item => item.productNo === message.productNo && item.scaleNo === scaleNo)))
              const fPks = map(flatten(map(this.viewCartItems).filter(item => item.productNo === message.productNo && item.scaleNo === scaleNo)))
              return {
                ...message,
                pks: [...pks, ...sPks, ...fPks],
                pic: viewProduct.pic,
                optionStr: viewCart?.optionStr || '',
                productName: viewProduct ? viewProduct.name : '',
                scaleName: viewSale ? viewSale.name : ''
              }
            })
          }
        }
        throw err
      })
    } else {
      return Promise.reject({
        code: -10009,
        message: 'fail'
      })
    }
  }

  /** 保存当前购物车，防止支付失败页进来 */
  saveResetCart () {
    this.saveCartItems = deepClone(this.cartItems)
  }
  /** 设置 */
  setCaveResetCartToCart () {
    this.cartItems = deepClone(this.saveCartItems)
  }

  /** 保留购物车部分数据， 清空购物车 */
  clearPart (clearData: any) {
    this.clear()
    this.info = { ...this.info, ...clearData }
  }

  /** 重算优惠及费 */
  async syncMkt (isForce: boolean = false) {
    // 未下单，已经有未下单的餐品都不重新算优惠
    // remove 210526  || this.cartItems.length
    if (!this.ordersId && !isForce) {
      return true
    }
    // 判断是否要重算 当前力度控制到实际支付金额是否有变化
    // 1. managerDiscount 发生变化 2. mktFee 发生变化
    const orderMemberId = parseInt(orderMobx.order.memberId)
    const cartMemberId = discountMobx.discountResult.memberId
    if (this.cartResult.paidPrice !== orderMobx.order.paidFee || this.cartResult.tax !== orderMobx.order.tax || cartMemberId !== orderMemberId) {
      const saveData = this.getSaveInfo()
      const theCartResultPaidPrice = this.cartResult.paidPrice
      const theCartResultTotalDiscountPrice = this.cartResult.totalDiscountPrice
      return orderAPI.calMktFee(saveData).then((res: any) => {
        if (res.result.paidFee !== theCartResultPaidPrice) {
          // throw 'sync error'
          console.error('同步优惠计算不一致')
          console.error(`cartPaidFee: ${theCartResultPaidPrice}, orderPaidFee: ${res.result.paidFee}`)
          console.error(`cartDiscount: ${theCartResultTotalDiscountPrice}, orderDiscount: ${toDecimal(sumBy(res.result.discountList, 'fee'))}`)
          return Promise.reject({
            code: -10008,
            message: 'fail'
          })
        } else {
          orderMobx.restore(res.result)
        }
        return true
      })
    } else {
      return true
    }
  }

  /** 撤销,桥接到订单的取消 */
  async cancel () {
    // 取消订单会引发购物车订单餐品的变化
    return orderMobx.cancel()
  }

  /** 撤销,桥接到订单的取消 */
  async cancelProduct (detail: { [detailId: string]: number }, reason: string) {
    return orderMobx.cancelProduct(detail, reason).then(() => {
      restaurantCenter.emit(RESTAURANT_EVENT.CART_ORDERITEMS_CHANGED, cartMobx.viewOrderItems)
    })
  }

  /** 设置快递费 */
  setExpressPrice (expressPrice?: number) {
    this.info.express = this.info.express || {}
    this.info.express.pk = this.info.express.pk || doPk()
    this.info.express.price = expressPrice
  }

  /** 设置联系人信息 */
  setContact (data?: {
    contactName?: string;
    contactMobile?: string;
    contactAddress?: string;
    contactApt?: string;
    contactEmail?: string;
    countryCode?: string;
    contactInfo?: any;
  }) {
    if (data) {
      this.info.contactName = data.contactName ?? (this.info.contactName || '')
      this.info.contactMobile = data.contactMobile ?? (this.info.contactMobile || '')
      this.info.contactAddress = data.contactAddress ?? (this.info.contactAddress || '')
      this.info.contactApt = data.contactApt ?? (this.info.contactApt || '')
      this.info.contactEmail = data.contactEmail ?? (this.info.contactEmail || '')
      this.info.countryCode = data.countryCode ?? (this.info.countryCode || '')
      this.info.contactInfo = data.contactInfo ?? (this.info.contactInfo || {})
    } else {
      this.info.contactName = ''
      this.info.contactMobile = ''
      this.info.contactAddress = ''
      this.info.contactApt = ''
      this.info.contactEmail = ''
      this.info.countryCode = ''
      this.info.contactInfo = {}
    }
  }

  /** 设置联系人获取省市code信息 */
  setContactLoading (type) {
    this.contactLoading = type
  }

  /** 设置收货信息 */
  async setAddressInfo (info?: IDeliveryAddressInfo) {
    if (info) {
      if (!info.contactInfo.extra) {
        this.setContactLoading(true)
        let cnAreaInfo = this.geCnAreaCodeForAddressInfo(info)
        const res = await LocationApi.getCnAreaCodeByName(cnAreaInfo)
        console.log('-----------getCnAreaCodeByName-----------')
        if (res) {
          info.contactInfo = {
            ...info.contactInfo,
            extra: {
              countyCode: res.countyCode,
              cityCode: res.cityCode,
              provinceCode: res.provinceCode
            }
          }
        }
        this.setContactLoading(false)
      }
      this.setContact(pick(info, 'contactAddress', 'contactMobile', 'contactName', 'contactInfo'))
    } else {
      this.setContact()
    }
  }

  // 提取省市县名称
  geCnAreaCodeForAddressInfo (info: IDeliveryAddressInfo) {
    const reg = /.+?(省|市|自治区|自治州|县|区)/g
    const cityName = info.contactInfo.city
    const provinceName = info.contactInfo.province
    const countyName = (info.contactInfo.address.match(reg) || [provinceName, cityName, ''])[!cityName ? 1 : 2] || ''
    return {
      provinceName,
      cityName,
      countyName,
    } as {
      cityName?: string;
      provinceName?: string;
      countyName?: string;
    }
  }

  /** 联系收货地址是否填写 */
  isContactInfoFill () {
    this.contactError = {
      nameError: !this.info.contactName,
      mobileError: !this.info.contactMobile,
      addressError: isEmpty(this.info.contactInfo)
    }

    if (Object.values(this.contactError).every(item => !item)) {
      return true
    }
    return false
  }

  /** 根据viewItems的Pk 设置当前购物车中餐品的remark */
  setCartProductRemark (data: Record<string, string>) {
    this.cartItems = this.cartItems.map(cartItem => {
      if (data[cartItem.pk] && cartItem.remark !== data[cartItem.pk]) {
        const newCartItem = { ...cartItem, remark: data[cartItem.pk] || '' }
        restaurantCenter.emit(restaurantCenter.RESTAURANT_EVENT.CART_ITEMS_CHANGED, {
          type: 'change',
          cartItem: newCartItem
        })
        return newCartItem
      } else {
        return cartItem
      }
    })
  }

  /** 将订单中的餐品还原回购物车的统一转换方法 */
  private convertOrderItemToCartItem = convertOrderItemToCartItem

  /** 将未接单的订单(其中可能包含未接单的餐品)还原回购物车的统一转换方法 */
  private convertSaveItemToCartItem = convertSaveItemToCartItem

  /** 会员订单到购物车 */
  restoreOrderToCart () {
    if (orderMobx.viewDetailList.length) {
      this.cartItems = orderMobx.viewDetailList.map(this.convertOrderItemToCartItem)
      this.ordersId = orderMobx.order.ordersId
      this.restoreOrderInfo()
      this.makeHash()
    }
  }

  /** 安全清空（不会发出清空购物车的事件） */
  safeClear () {
    this.cartItems = []
  }

  /** 返回一个当前购物车的数据模型 */
  get (): IMallCartModel {
    return {
      ordersId: this.ordersId,
      info: this.info,
      cartItems: this.viewCartItems,
      orderItems: this.viewOrderItems,
      result: this.cartResult
    }
  }

  setRemark (remark?: string, fromSync = false) {
    this.info.remark = remark || ''
    restaurantCenter.emit(RESTAURANT_EVENT.CART_INFO_CHANGED, {
      remark: this.info.remark,
      fromSync
    })
  }

  getHash () {
    return JSON.stringify(toJS(this.cartItems)) + JSON.stringify(toJS(this.info))
  }

  /** 标记一个购物车的标记位，优化是否需要重新下单的处理 */
  makeHash () {
    this.lastHash = this.getHash()
  }

  setIsMixture (isMixture: boolean) {
    this.isMixture = isMixture
  }
}
setTimeout(() => {
  autorun(() => {
    // HACK 当前在下单后由于先付费会将订单还原到orderMobx.order,使用此变量防止一次计算
    if (cartMobx.info || cartMobx.cartItems) {
      // cartMobx.lastUpdated = Date.now()
      restaurantCenter.emit(RESTAURANT_EVENT.CART_CHANGED, {
        info: toJS(cartMobx.info),
        cartItems: toJS(cartMobx.cartItems)
      })
    }
  })
})
export default new Cart()
export { createCartItem, getViewItems }
